import { Box, MenuItem, Select, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { history } from 'app';
import { TARGET_MAIN_OPTION } from 'new/constants/general';
import useActiveOffice from 'new/hooks/useActiveOffice';
import { useLocationSelector } from 'new/hooks/useLocationSelector';
import { useSidebarStore } from 'new/hooks/useSidebarStore';
import { useGetLocationGroups } from 'new/modules/ManageGroups/hooks';
import { useGetLocations } from 'new/modules/ManageLocations/hooks';
import { useGetUserAssignments } from 'new/modules/ManageUsers/hooks';
import { UserFromTokenResponse } from 'new/modules/MembershipPlan/types';
import findRoute from 'new/routing/routes';
import React, { useEffect } from 'react';
import { withRouter } from 'react-router';
import { SelectOption } from './Form/Select';
import { BREADCRUMBS_HEIGHT } from './Headers/EnrollHeader';

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: BREADCRUMBS_HEIGHT,
  paddingLeft: theme.spacing(6),
  paddingTop: theme.spacing(2),
  locationSelect: {
    color: theme.palette.secondary.dark,
    fontWeight: 700,
    '.MuiSelect-select': {
      color: theme.palette.secondary.dark,
      fontWeight: 700,
      // marginBottom: 6
    },
  },
  '.css-1yy8vzx': {
    '&::before': {
      visibility: 'hidden',
    },
    '&:hover': {
      '&::not': {
        '&.Mui-disabled': {
          '&::before': {
            borderBottom: 0,
          },
        },
      },
    },
  },
  '.css-1cccqvr': {
    '&:focus': {
      backgroundColor: 'transparent',
    },
  },
  '.locationSelector': {
    color: theme.palette.secondary.dark,
    zIndex: 2,
    fontWeight: 700,
    '.MuiInput-root:before, .MuiInput-root:after': {
      borderBottom: 'none',
    },

    '.MuiSelect-select': {
      color: theme.palette.secondary.dark,
      fontWeight: 700,
      fontSize: '1.65rem',
      minHeight: 'auto',
    },
    '.css-3j91l8-MuiInputBase-root-MuiInput-root-MuiSelect-root': {
      '&::before': {
        // content: "''",
        visibility: 'hidden',
      },
    },
    '.css-1rxz5jq-MuiSelect-select-MuiInputBase-input-MuiInput-input': {
      backgroundColor: 'transparent',
      '&::focus': {
        backgroundColor: 'transparent',
      },
    },
  },
  '.topTitle': {
    color: theme.palette.secondary.dark,
    zIndex: 2,
    fontWeight: 700,

    '.MuiInput-root:before, .MuiInput-root:after': {
      borderBottom: 'none',
    },

    '.MuiSelect-select': {
      color: theme.palette.secondary.dark,
      fontWeight: 700,
    },
  },
  '.bottomTitle': {
    color: theme.palette.primary.main,
    fontSize: '1.75rem',
    marginTop: -8,
    zIndex: 2,
    fontWeight: 700,
    textTransform: 'uppercase',

    img: {
      maxHeight: 26,
      position: 'relative',
      top: -4,
    },
  },
}));

type UseLocationDataProps = {
  user?: UserFromTokenResponse;
  orgId: number;
};

export type TargetOption = SelectOption & {
  groupId?: number;
};

export const useInitSingleLocationSelector = () => {
  const { user, orgId } = useActiveOffice();
  const {
    setEnabled,
    setSelectedLocation,
    setSelectedLocationName,
    setTargetType,
  } = useLocationSelector();

  useEffect(() => {
    if (user?.data.otherOffices && user.data.otherOffices.length <= 1) {
      setEnabled(false);
      setSelectedLocation(orgId.toString());
      setSelectedLocationName(user?.data.officeName ?? '');
      setTargetType('location');
    }
  }, [orgId, user]);
};

export const useLocationData = ({ user, orgId }: UseLocationDataProps) => {
  const { data: locationGroups = [] } = useGetLocationGroups(orgId);
  const { data = [] } = useGetLocations(orgId, user?.data.userId);
  const { data: userAssignments } = useGetUserAssignments(
    Number(user?.data.userId),
  );
  const notSuper =
    user?.data?.type === 'office_admin' ||
    user?.data?.type === 'dentist_standard';
  const offices =
    data?.map((office) => ({
      value: String(office.id),
      label: office.locationName,
      shouldFinishSetup: office.shouldFinishSetup,
    })) || [];

  const officesWithoutGroups =
    data
      ?.filter((office) => !office.groupId)
      .map((office) => ({
        value: String(office.id),
        label: office.locationName,
        shouldFinishSetup: office.shouldFinishSetup,
      })) || [];

  const locations =
    userAssignments?.locations?.map((location) => ({
      value: String(location.id),
      label: location.name,
      shouldFinishSetup: location.shouldFinishSetup,
    })) || [];

  const groups =
    locationGroups?.map((group) => ({
      value: String(group.id),
      label: '[G] ' + group.name,
    })) || [];

  const superOfficesWithLocations = offices.concat(locations);

  const officesWithLocations = notSuper ? locations : superOfficesWithLocations; //notSuper ? locations :
  const officesWithLocationsAndGroups = groups.concat(officesWithoutGroups);
  const uniqueOffices = officesWithLocations.filter(
    (loc, index, array) =>
      array.findIndex((item) => item.value === loc.value) === index,
  );
  const uniqueSupers = superOfficesWithLocations.filter(
    (loc, index, array) =>
      array.findIndex((item) => item.value === loc.value) === index,
  );
  const uniqueOfficesAndGroups = officesWithLocationsAndGroups.filter(
    (loc, index, array) =>
      array.findIndex((item) => item.value === loc.value) === index,
  );

  const allTargetsHierarchy: TargetOption[] = [
    ...groups.reduce(
      (acc, group) => [
        ...acc,
        group,
        ...data
          .filter((office) => office.groupId?.toString() === group.value)
          .map((office) => ({
            value: String(office.id),
            label: office.locationName,
            groupId: office.groupId,
            shouldFinishSetup: office.shouldFinishSetup,
          })),
      ],
      [] as SelectOption[],
    ),
    ...officesWithoutGroups,
  ];

  const masterTargetsHierarchy: TargetOption[] = [
    ...groups.reduce((acc, group) => [...acc, group], [] as SelectOption[]),
  ];

  return {
    locationGroups,
    offices,
    locations,
    groups,
    officesWithLocations,
    officesWithLocationsAndGroups,
    uniqueOffices,
    uniqueOfficesAndGroups,
    allTargetsHierarchy,
    masterTargetsHierarchy,
    hasMultiOffices: uniqueSupers.length > 1,
  };
};

export type LocationHeaderProps = {
  bottomTitle: string;
  patientOfficeName?: string;
  addMemberIcon?: boolean;
  hasGlobalSearch?: boolean;
  hasGlobalHelp?: boolean;
};

const LocationHeader: React.FC<LocationHeaderProps> = ({
  bottomTitle,
  patientOfficeName,
}) => {
  const {
    enabled,
    selectedLocation,
    setSelectedLocation,
    selectedLocationName,
    setSelectedLocationName,
    targetType,
    setTargetType,
    setSelectedTargetLocationCount,
  } = useLocationSelector();
  const {
    user,
    activeOfficeId,
    isSuperAdmin,
    setActiveLocationId,
    setViewAsLocationId,
    orgId,
    isPatient,
  } = useActiveOffice();

  const { viewAsStandard, tempViewAsMulti, setViewAsStandard } =
    useSidebarStore();

  useEffect(() => {
    if (
      user?.data.useMulti == false &&
      viewAsStandard == false &&
      tempViewAsMulti == false
    ) {
      setViewAsStandard(true);
    }
  }, [user?.data.useMulti, viewAsStandard, tempViewAsMulti]);

  const { orgName } = user?.data?.dentistInfo || {};
  const upgradedOffice = user?.data.tieredPayment?.tierLevel != 0;
  const isAdmin = user?.data?.type === 'dentist';
  const isBottomTitleImage = bottomTitle.startsWith('data:image');

  const {
    query: { id },
  } = history.getCurrentLocation();

  const {
    offices,
    officesWithLocationsAndGroups,
    uniqueOffices,
    allTargetsHierarchy,
  } = useLocationData({ user, orgId: Number(orgId) });

  useEffect(() => {
    const locationOrOffice = allTargetsHierarchy.find(
      (item) => item.value === selectedLocation,
    );
    const locationName =
      selectedLocation === TARGET_MAIN_OPTION.value
        ? TARGET_MAIN_OPTION.label
        : locationOrOffice && locationOrOffice.label
        ? locationOrOffice.label
        : 'UnIdentified';

    if (selectedLocationName !== locationName) {
      setSelectedLocationName(locationName);
    }
    const newTargetType =
      selectedLocation === TARGET_MAIN_OPTION.value
        ? 'master'
        : locationName.includes('[G]')
        ? 'group'
        : 'location';
    if (targetType != newTargetType) {
      setTargetType(newTargetType);
    }
    if (newTargetType === 'master') {
      setSelectedTargetLocationCount(
        allTargetsHierarchy.filter((trg) => !trg.label?.includes('[G]')).length,
      );
    }
    if (newTargetType === 'group') {
      setSelectedTargetLocationCount(
        allTargetsHierarchy.filter(
          (trg) => trg.groupId === parseInt(selectedLocation),
        ).length,
      );
    }
    if (newTargetType === 'location') {
      setSelectedTargetLocationCount(1);
    }
  }, [selectedLocation, JSON.stringify(allTargetsHierarchy)]);

  let title;

  if (isSuperAdmin && orgName && !viewAsStandard) {
    title = <Typography className="topTitle">{orgName}</Typography>;
  } else if (uniqueOffices.length > 1 && upgradedOffice) {
    title = (
      <Box className="topTitle">
        <Select
          variant="standard"
          value={activeOfficeId}
          onChange={(event) => {
            const { shouldFinishSetup } =
              uniqueOffices.find((item) => item.value === event.target.value) ||
              {};
            setViewAsLocationId(undefined);
            if (shouldFinishSetup) {
              history.push(findRoute('MultiSetupPage').path);
            } else {
              setActiveLocationId(event.target.value);
              history.push(findRoute('AnalyticDashboardPage').path);
            }
          }}
          MenuProps={{ PaperProps: { sx: { height: 'auto', maxHeight: 400 } } }}
        >
          {uniqueOffices.map(({ value, label }) => (
            <MenuItem key={label} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  } else if (!isPatient) {
    title = (
      <Typography className="topTitle">
        {patientOfficeName ??
          offices.find((item) => item.value === activeOfficeId)?.label}
      </Typography>
    );
  }

  return (
    <StyledBox>
      {title}
      <Typography className="bottomTitle">
        {isBottomTitleImage ? (
          <img src={bottomTitle} alt="logo" />
        ) : (
          <Box>
            <span>{bottomTitle}</span>
            {isAdmin &&
            officesWithLocationsAndGroups.length > 1 &&
            enabled &&
            (viewAsStandard == false || tempViewAsMulti == true)
              ? ': '
              : null}
            {isAdmin &&
              uniqueOffices.length > 1 &&
              enabled &&
              (viewAsStandard == false || tempViewAsMulti == true) && (
                <Box
                  className="locationSelector"
                  display={'inline'}
                  fontSize="1.75rem"
                >
                  {id != -4 ? (
                    <Select
                      variant="standard"
                      value={selectedLocation}
                      onChange={(event) => {
                        setSelectedLocation(event.target.value);
                      }}
                      MenuProps={{
                        PaperProps: { sx: { height: 'auto', maxHeight: 400 } },
                      }}
                    >
                      <MenuItem
                        value={TARGET_MAIN_OPTION.value}
                        sx={{ paddingLeft: 2 }}
                      >
                        {TARGET_MAIN_OPTION.label}
                      </MenuItem>
                      {allTargetsHierarchy.map(({ value, label, groupId }) => {
                        return (
                          <MenuItem
                            key={value}
                            value={value}
                            sx={{ paddingLeft: groupId ? 9 : 5.5 }}
                          >
                            {label}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  ) : (
                    <>Update Legacy Plans</>
                  )}
                </Box>
              )}
          </Box>
        )}
      </Typography>
    </StyledBox>
  );
};

export default withRouter(LocationHeader);
